import { useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { CONDITION_EXCLUDE, CONDITION_INCLUDE, GIVEN_TIME_TYPE_LUNCH, GIVEN_TIME_TYPE_NONE, WEEKDAY_LABELS } from '../constants';
import { DisplayTimingGroupListItem, DisplayTimingGroupOption } from '../models';
import TargetMenuDisplayTimingGroupModal from './TargetMenuDisplayTimingGroupModal';

interface DisplayTimingGroupRowProps {
  item: DisplayTimingGroupListItem;
  index: number;
  isFirst: boolean;
  isLast: boolean;
  groups?: DisplayTimingGroupOption[];
  onMove: (index: number, targetIndex: number) => void;
  onSelectRow: (event: React.ChangeEvent<HTMLInputElement>) => void;
  checkboxRef: (el: HTMLInputElement) => void;
  onReloadData?: () => void;
}

function renderWeekday(item: DisplayTimingGroupListItem) {
  if (item.weekday_mask === 0) return '-';

  const text = WEEKDAY_LABELS.filter(([bit]) => (item.weekday_mask & bit) !== 0)
    .map(([, label]) => label)
    .join('');

  return item.weekday_condition === CONDITION_EXCLUDE ? (
    <span className="d-inline-block bg-violet p-1 rounded mb-1">{text}</span>
  ) : (
    text
  );
}

function isPeriodExpired(item: DisplayTimingGroupListItem) {
  if (!item.period_enabled || !item.period_end) return false;

  const today = new Date().toISOString().slice(0, 10);
  return item.period_end < today;
}

function renderDateRange(item: DisplayTimingGroupListItem) {
  if (!item.period_enabled && !item.monthly_day_enabled) return '-';

  const lines: React.ReactNode[] = [];

  if (item.period_enabled && item.period_start && item.period_end) {
    const periodText = `${item.period_start.replaceAll('-', '/')} ~ ${item.period_end.replaceAll('-', '/')}`;
    const isExclude = item.period_condition === CONDITION_EXCLUDE;
    const text = isExclude ? (
      <span className="d-inline-block bg-violet p-1 rounded mb-1">{periodText}</span>
    ) : (
      periodText
    );
    lines.push(isPeriodExpired(item) ? <strong className="text-danger">{text}</strong> : text);
  }

  if (item.monthly_day_enabled) {
    const monthlyText = `毎月 ${item.monthly_days.join('、')} 日`;
    lines.push(
      item.monthly_day_condition === CONDITION_EXCLUDE ? (
        <span className="d-inline-block bg-violet p-1 rounded mb-1">{monthlyText}</span>
      ) : (
        monthlyText
      )
    );
  }

  return lines.map((line, index) => <div key={index}>{line}</div>);
}

function renderTimeRange(item: DisplayTimingGroupListItem) {
  if (item.given_time_type === GIVEN_TIME_TYPE_NONE) return '-';

  if (item.given_time_type === GIVEN_TIME_TYPE_LUNCH) {
    const lunchText = 'ランチ時間帯';
    const lunchExtendText = `猶予時間: ${item.given_lunch_extend_time}分`;
    return (
      <>
        <div>
          {item.given_lunch_condition === CONDITION_EXCLUDE ? (
            <span className="d-inline-block bg-violet p-1 rounded mb-1">{lunchText}</span>
          ) : (
            lunchText
          )}
        </div>
        {item.given_lunch_condition === CONDITION_INCLUDE && <div>{lunchExtendText}</div>}
      </>
    );
  }

  const rangeText = `${item.given_range_start} ~ ${item.given_range_end}`;
  const extendText = `猶予時間: ${item.given_range_extend_time}分`;

  return (
    <>
      <div>
        {item.given_range_condition === CONDITION_EXCLUDE ? (
          <span className="d-inline-block bg-violet p-1 rounded mb-1">{rangeText}</span>
        ) : (
          rangeText
        )}
      </div>
      {item.given_range_condition === CONDITION_INCLUDE && <div>{extendText}</div>}
    </>
  );
}

function DisplayTimingGroupRow({
  item,
  index,
  isFirst,
  isLast,
  groups,
  onMove,
  onSelectRow,
  checkboxRef,
  onReloadData,
}: DisplayTimingGroupRowProps) {
  const { t } = useTranslation();

  // states
  const [showTargetMenuModal, setShowTargetMenuModal] = useState(false);

  // events
  function handleCloseTargetMenuModal(isDataChanged: boolean) {
    setShowTargetMenuModal(false);

    if (isDataChanged) {
      onReloadData?.();
    }
  }

  return (
    <tr className={!item.is_active ? 'table-secondary' : isPeriodExpired(item) ? 'table-danger' : undefined}>
      <td className="text-center">
        <Form.Check
          ref={checkboxRef}
          name="selected_wizard"
          className="custom-checkbox"
          value={item.id}
          onChange={onSelectRow}
        ></Form.Check>
      </td>
      <td className="text-center">{index + 1}</td>
      <td>
        <div className="d-flex justify-content-center gap-4">
          <Button
            className="border"
            variant="secondary"
            style={{ minWidth: 'inherit', width: '3rem' }}
            type="button"
            disabled={isFirst}
            onClick={() => onMove(index, index - 1)}
          >
            ▲
          </Button>

          <Button
            className="border"
            variant="secondary"
            style={{ minWidth: 'inherit', width: '3rem' }}
            type="button"
            disabled={isLast}
            onClick={() => onMove(index, index + 1)}
          >
            ▼
          </Button>
        </div>
      </td>
      <td className="text-center">{item.group_name}</td>
      <td className={`text-center${item.is_active ? ' bg-yellow' : ''}`}>{item.is_active ? '有効' : '無効'}</td>
      <td className="text-center">{renderWeekday(item)}</td>
      <td className="text-center">{renderDateRange(item)}</td>
      <td className="text-center">{renderTimeRange(item)}</td>
      <td>
        <div className="d-flex justify-content-between align-items-center">
          <span>{item.menu_count}件</span>
          <Button className="ms-5" onClick={() => setShowTargetMenuModal(true)}>
            {t('DisplayTimingGroup.target_menu_setting')}
          </Button>
        </div>
        {showTargetMenuModal &&
          createPortal(
            <TargetMenuDisplayTimingGroupModal
              displayTimingGroupId={item.id}
              groupName={item.group_name}
              groups={groups}
              onClose={handleCloseTargetMenuModal}
            />,
            document.body
          )}
      </td>
    </tr>
  );
}

export default DisplayTimingGroupRow;
